import type { LayoutElement } from "../../Dashboard";
import type { SerializedWorkbenchSessionState } from "../PrivateWorkbenchSession.schema";

export enum WorkbenchSessionSource {
    LOCAL_STORAGE = "localStorage",
    BACKEND = "backend",
}

export type WorkbenchSessionDataContainer = SerializedWorkbenchSessionState &
    (
        | {
              id?: string; // Optional ID for the session (only when stored once), can be used to identify or restore the session
              source: WorkbenchSessionSource.LOCAL_STORAGE; // Source of the session data
          }
        | {
              id: string; // Required ID for the session (when stored multiple times), used to identify or restore the session
              source: WorkbenchSessionSource.BACKEND; // Source of the session data
              isSnapshot: boolean; // Indicates if this session is a snapshot
          }
    );

export function isPersisted(
    session: WorkbenchSessionDataContainer,
): session is Extract<WorkbenchSessionDataContainer, { id: string }> {
    return typeof session.id === "string" && session.id.length > 0;
}

export function isFromBackend(
    session: WorkbenchSessionDataContainer,
): session is Extract<WorkbenchSessionDataContainer, { source: WorkbenchSessionSource.BACKEND }> {
    return session.source === WorkbenchSessionSource.BACKEND;
}

export function isSnapshot(
    session: WorkbenchSessionDataContainer,
): session is Extract<WorkbenchSessionDataContainer, { isSnapshot: true }> {
    // Check if the session has the isSnapshot property set to true
    if (session.source !== WorkbenchSessionSource.BACKEND) {
        return false; // Only backend sessions can be snapshots
    }
    return session.isSnapshot === true;
}

function extractLayoutFromSerializedDashboard(
    dashboard: SerializedWorkbenchSessionState["content"]["dashboards"][number],
): LayoutElement[] {
    const layout: LayoutElement[] = [];

    for (const serializedInstance of dashboard.moduleInstances) {
        const {
            moduleInstanceState: { id, name },
            layoutState,
        } = serializedInstance;

        layout.push({
            moduleInstanceId: id,
            moduleName: name,
            relX: layoutState.relX,
            relY: layoutState.relY,
            relHeight: layoutState.relHeight,
            relWidth: layoutState.relWidth,
            minimized: layoutState.minimized,
            maximized: layoutState.maximized,
        });
    }

    return layout;
}

export type DashboardPreviewItem = {
    id: string;
    name: string;
    description?: string;
    layout: LayoutElement[];
};

export function extractDashboardPreviewItems(session: WorkbenchSessionDataContainer): DashboardPreviewItem[] {
    return session.content.dashboards.map((dashboard) => ({
        id: dashboard.id,
        name: dashboard.name,
        description: dashboard.description,
        layout: extractLayoutFromSerializedDashboard(dashboard),
    }));
}
